import { useEffect, useRef, useState } from 'react';

export interface EstimatedProgressOptions {
  /** Where the estimate stops climbing while the real work is still running. */
  cap?: number;
  /** Re-render cadence in ms. */
  tickMs?: number;
  /** Exponential approach rate: higher reaches the cap sooner within the ETA. */
  decay?: number;
  /**
   * When the job started (epoch ms), for callers whose job outlives this
   * component — pass it and the estimate survives an unmount/remount instead of
   * restarting from 0%. Omitted ⇒ anchored to when `active` flipped on.
   */
  startedAt?: number | null;
}

/**
 * Smooth estimated progress (0–cap) for a server-side job that reports no real
 * percentage — the time-interpolation idiom shared by the publish drawer, the
 * dub modal and the music separation row (design-system.md "Modal footer
 * progress"). While `active`, the value approaches `cap` along
 * 1 − e^(−decay·elapsed/eta), never decreases (ratchet), and resets when
 * `active` flips on again. Callers show it as "label · NN%" over a thin
 * ProgressBar and snap to done when the job actually completes.
 */
export function useEstimatedProgress(
  active: boolean,
  etaMs: number,
  {
    cap = 95,
    tickMs = 500,
    decay = 2,
    startedAt,
  }: EstimatedProgressOptions = {},
): number {
  const [, tick] = useState(0);
  // Only the anchor for callers that don't own the start time themselves; a
  // caller-supplied `startedAt` wins, so remounting doesn't rewind the estimate.
  const activatedAtRef = useRef<number | null>(null);
  const lastPercentRef = useRef(0);

  // Clear the ratchet on the render that STARTS a run, not in the effect below.
  // An effect lands a render late and writes no state, so a consumer that
  // outlives its runs — `DubRunChip` never unmounts, and the dub popover stays
  // open across a retry — painted the previous run's high-water mark (95%) for
  // one tick before dropping back. Left here on `active` going false instead,
  // the value would rewind under any consumer still showing the bar.
  const wasActiveRef = useRef(active);
  if (active && !wasActiveRef.current) lastPercentRef.current = 0;
  wasActiveRef.current = active;

  useEffect(() => {
    activatedAtRef.current = active ? Date.now() : null;
  }, [active]);

  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => tick(t => t + 1), tickMs);
    return () => window.clearInterval(id);
  }, [active, tickMs]);

  const start = startedAt ?? activatedAtRef.current;
  if (!active || start == null) return lastPercentRef.current;

  const elapsedMs = Math.max(0, Date.now() - start);
  const fraction = etaMs > 0 ? 1 - Math.exp((-decay * elapsedMs) / etaMs) : 0;
  const raw = Math.min(cap * fraction, cap);
  const next = Math.max(lastPercentRef.current, raw);
  lastPercentRef.current = next;
  return next;
}
